'use client';

/**
 * RFQ Comparison Widget
 *
 * @component_role Vendor Quote Comparison Matrix with Award-to-PO Workflow
 * @business_rule Highlights lowest price per product line item for procurement decisions.
 *   Green cells indicate best price among responding vendors.
 *
 *   Two add-to-cart options:
 *   - "Award All": In vendor header, adds all quoted products from that vendor (clears cart first)
 *   - "Add": In each cell, adds single product at quoted price (appends to cart)
 *
 * @user_outcome Compare vendor responses side-by-side, then either award full order to
 *   preferred vendor or cherry-pick best prices per product across vendors.
 * @integration_point klyde_edge /neo/graph/compare/rfq - retrieves vendor response matrix from Neo4j
 * @integration_point cart-store - populates cart with line items for PO creation
 *
 * @ux_pattern Section 18.3 Sticky Behaviors - product column and header row stay visible
 *   during scroll. Shadow hints appear when content is scrolled behind sticky elements.
 * @ux_pattern Cell hover reveals Add button for single-product cart addition.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain procurement_intelligence
 */

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useRFQComparison } from '@/lib/api/hooks';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import type { ComparisonVendor, ComparisonProduct, ComparisonCell } from '@/lib/api/types';
import { Loader2, AlertCircle, TrendingDown, TrendingUp, Clock, Check, X, ChevronDown, ShoppingCart, Plus, Zap, Trash2, ArrowRight, LayoutGrid } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils/cn';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useCartStore } from '@/lib/stores/cart-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { openComposerSurface } from '@/lib/stores/rail-surface-store';

interface RFQComparisonWidgetProps {
  entityId: string;
  title?: string;
  /** When provided, skip internal fetch and use this data directly. */
  externalData?: RFQCompareResponse | null;
  externalLoading?: boolean;
}

type SortField = 'name' | 'price' | 'lead_time';
type SortDir = 'asc' | 'desc';

// Import the full response type for external data
import type { RFQCompareResponse } from '@/lib/api/types';

export function RFQComparisonWidget({ entityId, title, externalData, externalLoading }: RFQComparisonWidgetProps) {
  const rfqComparisonStrategy = useCapabilityStore((s) => s.strategyName('rfi', 'comparison'));
  const internal = useRFQComparison(rfqComparisonStrategy!, externalData !== undefined ? null : { rfq_id: entityId });
  const data = externalData !== undefined ? externalData : internal.data;
  const isLoading = externalData !== undefined ? (externalLoading ?? false) : internal.isLoading;
  const error = externalData !== undefined ? null : internal.error;
  const { contentKey } = useCardIdentity();
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [highlightBest, setHighlightBest] = useState(true);
  const [groupByCategory, setGroupByCategory] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);

  // MCP radio-select state: product_semantic_id → vendor_semantic_id
  const [selections, setSelections] = useState<Map<string, string>>(new Map());

  const toggleSelection = useCallback((productId: string, vendorId: string) => {
    setSelections(prev => {
      const next = new Map(prev);
      if (next.get(productId) === vendorId) {
        next.delete(productId);
      } else {
        next.set(productId, vendorId);
      }
      return next;
    });
  }, []);

  // Scroll state for shadow hints (Section 18.3)
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [hasScrollRight, setHasScrollRight] = useState(false);
  const [hasScrollBelow, setHasScrollBelow] = useState(false);

  // Track scroll position for shadow hints
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Check if there's content scrolled to the right
    setHasScrollRight(container.scrollLeft > 0);

    // Check if there's content scrolled below
    setHasScrollBelow(container.scrollTop > 0);
  }, []);

  // Set up scroll listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Initial check
    handleScroll();

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll, data]);

  // Connect to graph selection store for copilot panel
  const selectNode = useGraphSelectionStore((state) => state.selectNode);

  // Cart store for Award All functionality
  const cartStore = useCartStore();
  const activeMode = useLayoutStore((state) => state.activeExplorerView);

  // Handle "Award All" to a vendor - populate cart with all their quoted items
  const handleAwardAll = useCallback((vendor: ComparisonVendor) => {
    if (!data) return;

    // Clear cart and set to PO mode
    cartStore.clear();
    cartStore.setCartType('po');

    // Add the vendor
    cartStore.addVendor(vendor.semantic_id, vendor.name, vendor.email);

    // Add all products that have quotes from this vendor
    data.products.forEach((product) => {
      const cell = data.matrix[product.semantic_id]?.[vendor.semantic_id];
      if (cell && cell.unit_price !== null) {
        cartStore.addItem({
          product_sid: product.semantic_id,
          name: product.name,
          quantity: cell.quantity_offered || product.requested_quantity || 1,
          vendor_id: vendor.semantic_id,
          vendor_name: vendor.name,
          list_price: cell.unit_price,
          sku: product.sku || undefined,
        });
      }
    });

    // Open the cart in the rail composer — the drawer is retired.
    openComposerSurface(activeMode);
  }, [data, cartStore, activeMode]);

  // Handle single product add to cart
  const handleAddSingle = useCallback((product: ComparisonProduct, vendor: ComparisonVendor, cell: ComparisonCell) => {
    if (!cell.unit_price) return;

    // Add vendor if not already in cart
    cartStore.addVendor(vendor.semantic_id, vendor.name, vendor.email);

    // Add the product
    cartStore.addItem({
      product_sid: product.semantic_id,
      name: product.name,
      quantity: cell.quantity_offered || product.requested_quantity || 1,
      vendor_id: vendor.semantic_id,
      vendor_name: vendor.name,
      list_price: cell.unit_price,
      sku: product.sku || undefined,
    });

    toast.success('Added to cart', {
      description: `${product.name} from ${vendor.name}`,
    });

    // Make the cart visible in the rail composer.
    openComposerSurface(activeMode);
  }, [cartStore, activeMode]);

  // Commit radio-selections to cart as MCP items
  const handleCommitToMCP = useCallback(() => {
    if (!data || selections.size === 0) return;

    cartStore.clear();
    cartStore.setCartType('mcp');

    // Collect unique vendors
    const vendorIds = new Set(selections.values());
    const vendorMap = new Map(data.vendors.map(v => [v.semantic_id, v]));

    for (const vid of vendorIds) {
      const v = vendorMap.get(vid);
      if (v) cartStore.addVendor(v.semantic_id, v.name, v.email);
    }

    // Add each selected line item
    for (const [productId, vendorId] of selections) {
      const product = data.products.find(p => p.semantic_id === productId);
      const vendor = vendorMap.get(vendorId);
      const cell = data.matrix[productId]?.[vendorId];
      if (!product || !vendor || !cell?.unit_price) continue;

      cartStore.addItem({
        product_sid: product.semantic_id,
        name: product.name,
        quantity: cell.quantity_offered || product.requested_quantity || 1,
        vendor_id: vendor.semantic_id,
        vendor_name: vendor.name,
        list_price: cell.unit_price,
        sku: product.sku || undefined,
      });
    }

    openComposerSurface(activeMode);
    toast.success('Cost plan populated', {
      description: `${selections.size} items from ${vendorIds.size} vendor${vendorIds.size !== 1 ? 's' : ''}`,
    });
  }, [data, selections, cartStore, activeMode]);

  // Selection totals
  const selectionTotal = useMemo(() => {
    if (!data || selections.size === 0) return 0;
    let total = 0;
    for (const [productId, vendorId] of selections) {
      const cell = data.matrix[productId]?.[vendorId];
      if (cell?.unit_price) total += cell.unit_price;
    }
    return total;
  }, [data, selections]);

  // Handle product row click - show details in copilot pane
  const handleProductClick = (product: ComparisonProduct) => {
    setSelectedProductId(product.semantic_id);

    selectNode({
      id: product.semantic_id,
      label: product.name,
      type: 'product',
      properties: {
        sku: product.sku,
        category: product.category,
      }
    });
  };

  // Calculate vendor totals (exclude unmatched — not comparable)
  const vendorTotals = useMemo(() => {
    if (!data) return {};
    const totals: Record<string, number> = {};

    for (const vendor of data.vendors) {
      let total = 0;
      for (const product of data.products) {
        if (product.unmatched) continue;
        const cell = data.matrix[product.semantic_id]?.[vendor.semantic_id];
        if (cell?.unit_price) {
          total += cell.unit_price;
        }
      }
      totals[vendor.semantic_id] = total;
    }
    return totals;
  }, [data]);

  // Find best price per product (exclude unmatched — not comparable)
  const bestPrices = useMemo(() => {
    if (!data) return {};
    const best: Record<string, { vendorId: string; price: number }> = {};

    for (const product of data.products) {
      if (product.unmatched) continue;

      let minPrice = Infinity;
      let minVendor = '';

      for (const vendor of data.vendors) {
        const cell = data.matrix[product.semantic_id]?.[vendor.semantic_id];
        if (cell?.unit_price && cell.unit_price < minPrice) {
          minPrice = cell.unit_price;
          minVendor = vendor.semantic_id;
        }
      }

      if (minPrice < Infinity) {
        best[product.semantic_id] = { vendorId: minVendor, price: minPrice };
      }
    }
    return best;
  }, [data]);

  const handleAutoSelectBest = useCallback(() => {
    setSelections(new Map(
      Object.entries(bestPrices).map(([pid, { vendorId }]) => [pid, vendorId])
    ));
  }, [bestPrices]);

  const handleClearSelections = useCallback(() => {
    setSelections(new Map());
  }, []);

  // Find lowest total vendor
  const lowestTotalVendor = useMemo(() => {
    if (!data || Object.keys(vendorTotals).length === 0) return null;
    let minTotal = Infinity;
    let minVendor = '';

    for (const [vendorId, total] of Object.entries(vendorTotals)) {
      if (total < minTotal && total > 0) {
        minTotal = total;
        minVendor = vendorId;
      }
    }
    return minVendor || null;
  }, [data, vendorTotals]);

  // Sort products
  const sortedProducts = useMemo(() => {
    if (!data) return [];

    return [...data.products].sort((a, b) => {
      // Unmatched vendor-only items always last
      const aUnmatched = a.unmatched ? 2 : 0;
      const bUnmatched = b.unmatched ? 2 : 0;
      if (aUnmatched !== bUnmatched) return aUnmatched - bUnmatched;

      // Virtual products (VP_*) second-to-last
      const aVirtual = a.semantic_id.startsWith('VP_') ? 1 : 0;
      const bVirtual = b.semantic_id.startsWith('VP_') ? 1 : 0;
      if (aVirtual !== bVirtual) return aVirtual - bVirtual;

      let cmp = 0;
      if (sortField === 'name') {
        cmp = a.name.localeCompare(b.name);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, sortField, sortDir]);

  // Group products by category when toggle is on
  type DisplayRow =
    | { type: 'category'; label: string; count: number }
    | { type: 'product'; product: ComparisonProduct };

  const displayRows: DisplayRow[] = useMemo(() => {
    if (!groupByCategory) return sortedProducts.map(p => ({ type: 'product' as const, product: p }));

    const groups = new Map<string, ComparisonProduct[]>();
    for (const p of sortedProducts) {
      const cat = p.category || 'Uncategorized';
      if (!groups.has(cat)) groups.set(cat, []);
      groups.get(cat)!.push(p);
    }

    const rows: DisplayRow[] = [];
    for (const [label, products] of groups) {
      rows.push({ type: 'category', label, count: products.length });
      for (const product of products) {
        rows.push({ type: 'product', product });
      }
    }
    return rows;
  }, [sortedProducts, groupByCategory]);

  // Scene export — register the RFQ comparison (obeya_matrix on canvas,
  // rfq_matrix on the rfq selection) into the capture envelope. After all
  // hooks, before the first early return (Rules of Hooks). Scene-only when
  // fed external data; otherwise query_key mirrors the rfi_comparison fetch.
  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'rfq_matrix',
    status: (data ? 'loaded' : entityId ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: externalData === undefined && rfqComparisonStrategy && entityId
      ? ['strategy-data', rfqComparisonStrategy, { rfq_id: entityId }]
      : null,
    text: data
      ? `${data.products?.length ?? 0} products · ${Object.keys(vendorTotals).length} vendors`
      : (title ?? 'RFQ comparison'),
    json: {
      rfq_id: entityId ?? null,
      products: data?.products?.length ?? 0,
      vendors: Object.keys(vendorTotals).length,
      lowest_total_vendor: lowestTotalVendor ?? null,
    },
  }), [contentKey, data, entityId, externalData, rfqComparisonStrategy, title, vendorTotals, lowestTotalVendor]));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        <span className="ml-2 text-xs text-muted-foreground">Loading comparison...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-destructive">
        <AlertCircle className="w-5 h-5 mb-2" />
        <p className="text-xs">Failed to load comparison</p>
        <p className="text-[11px] text-muted-foreground">{String(error)}</p>
      </div>
    );
  }

  if (!data || data.vendors.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <AlertCircle className="w-5 h-5 mb-2" />
        <p className="text-xs">No vendor responses yet</p>
        <p className="text-[11px]">Vendors need to submit quotes for this RFQ</p>
      </div>
    );
  }

  const formatCurrency = (value: number | null) => {
    if (value === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatLeadTime = (days: number | null) => {
    if (days === null) return '-';
    if (days === 1) return '1 day';
    return `${days} days`;
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-3 py-2 border-b border-panel-border bg-muted/30">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-bold">{data.rfq_name || title || 'RFQ Comparison'}</h2>
            <p className="text-[11px] text-muted-foreground">
              {data.vendors.length} vendor{data.vendors.length !== 1 ? 's' : ''} responded
              {' · '}
              {data.products.length} product{data.products.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleAutoSelectBest}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
              title="Auto-select lowest price per product"
            >
              <Zap className="w-3 h-3" />
              Auto-select best
            </button>
            <label className="flex items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                checked={highlightBest}
                onChange={(e) => setHighlightBest(e.target.checked)}
                className="rounded"
              />
              <span className="text-muted-foreground">Highlight best</span>
            </label>
            <button
              onClick={() => setGroupByCategory(g => !g)}
              className={cn(
                "flex items-center gap-1.5 px-2 py-1 rounded text-[11px] transition-colors",
                groupByCategory ? "bg-primary/10 text-primary font-medium" : "text-muted-foreground hover:text-foreground"
              )}
            >
              <LayoutGrid className="w-3 h-3" />
              By category
            </button>
          </div>
        </div>

        {/* Summary - only show when there are meaningful differences */}
        {data.summary && (
          <div className="flex items-center gap-3 mt-1 text-[11px]">
            {/* Best total: only show if there's a price difference */}
            {data.summary.best_price_vendor_name && data.summary.lowest_total !== data.summary.highest_total && (
              <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
                <TrendingDown className="w-3 h-3" />
                <span>Best total: {data.summary.best_price_vendor_name}</span>
              </div>
            )}
            {data.summary.avg_lead_time_days && (
              <div className="flex items-center gap-1 text-muted-foreground">
                <Clock className="w-3 h-3" />
                <span>Lead time: {formatLeadTime(data.summary.avg_lead_time_days)}</span>
              </div>
            )}
          </div>
        )}

      </div>

      {/* Comparison Table */}
      <div ref={scrollContainerRef} className="flex-1 overflow-auto">
        <table className="w-full border-collapse text-xs">
          <thead className={cn(
            "sticky top-0 z-20 transition-shadow duration-200",
            hasScrollBelow && "shadow-[0_4px_8px_rgba(0,0,0,0.1)]"
          )}>
            <tr>
              {/* Corner cell - sticky both left and top (z-30) */}
              <th className={cn(
                "text-left px-3 py-1.5 border-b border-panel-border font-medium min-w-[180px] bg-panel text-muted-foreground",
                "sticky left-0 z-30 transition-shadow duration-200",
                hasScrollRight && "shadow-[4px_0_8px_rgba(0,0,0,0.1)]"
              )}>
                Product
              </th>
              {data.vendors.map((vendor) => (
                <th
                  key={vendor.semantic_id}
                  className={cn(
                    "text-center px-2 py-1.5 border-b border-l border-panel-border min-w-[120px]",
                    highlightBest && lowestTotalVendor === vendor.semantic_id
                      ? "bg-green-50 dark:bg-green-950/30"
                      : "bg-panel"
                  )}
                >
                  <div className="font-bold text-foreground">{vendor.name}</div>
                  <div className="text-[11px] text-muted-foreground font-normal mb-0.5">
                    {vendor.line_count} items
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAwardAll(vendor);
                    }}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium rounded bg-primary/10 hover:bg-primary/20 text-primary transition-colors"
                  >
                    <ShoppingCart className="w-2.5 h-2.5" />
                    Award All
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, idx) => {
              if (row.type === 'category') {
                return (
                  <tr key={`cat-${row.label}`} className="bg-muted/50">
                    <td
                      colSpan={1 + data.vendors.length}
                      className="px-3 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-b border-t border-panel-border sticky left-0"
                    >
                      {row.label} ({row.count})
                    </td>
                  </tr>
                );
              }

              const product = row.product;
              const best = bestPrices[product.semantic_id];
              const isUnmatched = product.unmatched === true;

              const isSelected = selectedProductId === product.semantic_id;

              const hasMCPSelection = selections.has(product.semantic_id);

              // Separator row before first unmatched item (flat mode only)
              const prevRow = idx > 0 ? displayRows[idx - 1] : null;
              const prevProduct = prevRow?.type === 'product' ? prevRow.product : null;
              const showSeparator = !groupByCategory && isUnmatched && (!prevProduct || !prevProduct.unmatched);

              return (
                <React.Fragment key={product.semantic_id}>
                  {showSeparator && (
                    <tr>
                      <td
                        colSpan={1 + data.vendors.length}
                        className="px-3 py-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground bg-muted/50 border-b border-t border-panel-border"
                      >
                        Additional quoted items (not in RFQ)
                      </td>
                    </tr>
                  )}
                  <tr
                    onClick={() => handleProductClick(product)}
                    className={cn(
                      "hover:bg-accent/30 cursor-pointer transition-colors",
                      isSelected && "bg-primary/10 ring-1 ring-inset ring-primary/30",
                      hasMCPSelection && "border-l-2 border-l-primary",
                      isUnmatched && "opacity-60"
                    )}
                  >
                    {/* Product name cell - sticky left (z-10) */}
                    <td className={cn(
                      "px-3 py-2 border-b border-panel-border bg-panel",
                      "sticky left-0 z-10 transition-shadow duration-200",
                      hasScrollRight && "shadow-[4px_0_8px_rgba(0,0,0,0.1)]"
                    )}>
                      <div className="font-medium text-foreground">{product.name}</div>
                      {!isUnmatched && (
                        <div className="text-[10px] text-muted-foreground font-mono">{product.semantic_id}</div>
                      )}
                      {product.sku && (
                        <div className="text-[10px] text-muted-foreground">{product.sku}</div>
                      )}
                    </td>
                    {data.vendors.map((vendor) => {
                      const cell = data.matrix[product.semantic_id]?.[vendor.semantic_id];
                      const isBest = !isUnmatched && highlightBest && best?.vendorId === vendor.semantic_id;
                      const isLowestTotalVendor = !isUnmatched && highlightBest && lowestTotalVendor === vendor.semantic_id;
                      const isMCPSelected = selections.get(product.semantic_id) === vendor.semantic_id;
                      const hasQuote = cell && cell.unit_price !== null && cell.can_supply !== false;

                      return (
                        <td
                          key={vendor.semantic_id}
                          className={cn(
                            "text-center px-2 py-2 border-b border-l border-panel-border",
                            isMCPSelected
                              ? "bg-primary/5 ring-1 ring-inset ring-primary/20"
                              : isBest
                                ? "bg-green-50 dark:bg-green-950/30"
                                : isLowestTotalVendor
                                  ? "bg-green-50/50 dark:bg-green-950/20"
                                  : "bg-panel"
                          )}
                        >
                          {cell ? (
                            <div className="group/cell">
                              {/* Radio indicator + price on same line */}
                              <div className="flex items-center justify-center gap-1">
                                {hasQuote && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleSelection(product.semantic_id, vendor.semantic_id);
                                    }}
                                    className="shrink-0"
                                    title={isMCPSelected ? 'Deselect' : 'Select for cost plan'}
                                  >
                                    <div className={cn(
                                      "w-3.5 h-3.5 rounded-full border-2 transition-colors flex items-center justify-center",
                                      isMCPSelected
                                        ? "border-primary bg-primary"
                                        : "border-muted-foreground/40 hover:border-primary/60"
                                    )}>
                                      {isMCPSelected && (
                                        <div className="w-1.5 h-1.5 rounded-full bg-white" />
                                      )}
                                    </div>
                                  </button>
                                )}
                                <span className={cn(
                                  "font-medium",
                                  isBest && "text-green-600 dark:text-green-400"
                                )}>
                                  {formatCurrency(cell.unit_price)}
                                  {isBest && <Check className="inline w-2.5 h-2.5 ml-0.5" />}
                                </span>
                              </div>
                              {cell.lead_time_days && (
                                <div className="text-[11px] text-muted-foreground">
                                  {formatLeadTime(cell.lead_time_days)}
                                </div>
                              )}
                              {cell.can_supply === false && (
                                <div className="text-[11px] text-red-500 flex items-center justify-center gap-0.5">
                                  <X className="w-2.5 h-2.5" /> Cannot supply
                                </div>
                              )}
                              {/* Single product Add to Cart button */}
                              {hasQuote && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddSingle(product, vendor, cell);
                                  }}
                                  className="mt-0.5 opacity-0 group-hover/cell:opacity-100 inline-flex items-center gap-0.5 px-1 py-0.5 text-[10px] font-medium rounded bg-muted hover:bg-accent text-muted-foreground hover:text-foreground transition-all"
                                  title={`Add ${product.name} to cart`}
                                >
                                  <Plus className="w-2.5 h-2.5" />
                                  Add
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot className={cn(
            "sticky bottom-0 border-t-2 border-panel-border z-20"
          )}>
            <tr className="font-semibold text-xs">
              {/* TOTAL label - sticky left (z-30) */}
              <td className={cn(
                "px-3 py-2 bg-panel",
                "sticky left-0 z-30 transition-shadow duration-200",
                hasScrollRight && "shadow-[4px_0_8px_rgba(0,0,0,0.1)]"
              )}>TOTAL</td>
              {data.vendors.map((vendor) => {
                const total = vendorTotals[vendor.semantic_id] || 0;
                const isLowest = highlightBest && lowestTotalVendor === vendor.semantic_id;

                return (
                  <td
                    key={vendor.semantic_id}
                    className={cn(
                      "text-center px-2 py-2 border-l border-panel-border",
                      isLowest
                        ? "bg-green-50 dark:bg-green-950/30 text-green-600 dark:text-green-400"
                        : "bg-panel"
                    )}
                  >
                    {formatCurrency(total)}
                    {isLowest && <Check className="inline w-3 h-3 ml-0.5" />}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>

      {/* MCP Selection Basket Bar */}
      {selections.size > 0 && (
        <div className="px-3 py-2 border-t-2 border-primary/20 bg-primary/5 flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3 text-xs">
            <span className="font-semibold text-foreground uppercase tracking-wider">
              Selection
            </span>
            <span className="text-muted-foreground">
              {selections.size} of {data.products.length} lines
            </span>
            <span className="font-medium text-foreground">{formatCurrency(selectionTotal)}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleClearSelections}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
            >
              <Trash2 className="w-3 h-3" />
              Clear
            </button>
            <button
              onClick={handleCommitToMCP}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <ShoppingCart className="w-3 h-3" />
              Add to Cost Plan
              <ArrowRight className="w-2.5 h-2.5" />
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="px-3 py-1.5 border-t border-panel-border bg-muted/30 text-[10px] text-muted-foreground">
        Query: {data.metadata.query_time_ms}ms
        {' · '}
        Status: {data.metadata.rfq_status}
      </div>
    </div>
  );
}
